import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { Briefcase, TrendingUp, ShieldCheck, Globe, Target, CheckCircle, Zap } from 'lucide-react';

const StrategyPage: React.FC = () => {
  const { t, currentLanguage } = usePublicTranslation();
  const strategy = t?.about?.strategy;

  if (!strategy) return null;

  const StatusBadge = ({ status, labels }: { status: string, labels: any }) => {
    const styles = {
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'anticipated': 'bg-purple-100 text-purple-800 border-purple-200',
      'in-progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'planned': 'bg-amber-100 text-amber-800 border-amber-200',
      'future': 'bg-gray-100 text-gray-600 border-gray-200'
    };

    const labelText = {
      'completed': labels?.completed,
      'anticipated': labels?.anticipated,
      'in-progress': labels?.inProgress,
      'planned': labels?.planned,
      'future': labels?.future
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${styles[status as keyof typeof styles] || styles.future}`}>
        {labelText[status as keyof typeof labelText]}
      </span>
    );
  };

  useSEO({
    title: strategy.hero.title,
    description: strategy.hero.subtitle,
    path: '/about/strategy',
    language: currentLanguage as 'pt-br' | 'en' | 'es',
    structuredData: [
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Sobre', url: '/about' },
        { name: 'Estratégia', url: '/about/strategy' }
      ]),
      generateWebPageSchema({
        name: strategy.hero.title,
        description: strategy.hero.subtitle,
        url: '/about/strategy',
        datePublished: "2025-01-20",
        dateModified: "2025-01-20"
      })
    ]
  });

  return (
    <PublicLayout>

      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-primary text-white py-12 md:py-20 bg-gradient-to-br from-dark to-primary-dark relative overflow-hidden">
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold mb-4 md:mb-6 animate-fade-in-up text-transparent bg-clip-text bg-gradient-to-br from-white to-secondary">
              {strategy.hero.title}
            </h1>
            <p className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto text-gray-200 animate-slide-up-fade">
              {strategy.hero.subtitle}
            </p>
          </div>
        </div>

        {/* Business Model Section */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-primary text-center mb-8 md:mb-16">
              {strategy.businessModel.title}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 max-w-5xl mx-auto">
              {/* Scope Card */}
              <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 md:p-10 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-secondary/10 rounded-xl flex items-center justify-center text-secondary mb-4 md:mb-6">
                  <Briefcase size={28} className="md:hidden" />
                  <Briefcase size={32} className="hidden md:block" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-primary mb-3 md:mb-4">
                  {strategy.businessModel.scope.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-base md:text-lg">
                  {strategy.businessModel.scope.text}
                </p>
              </div>

              {/* Activities Card */}
              <div className="bg-primary/5 rounded-2xl p-6 sm:p-8 md:p-10 border border-primary/10 shadow-lg hover:shadow-xl transition-shadow duration-300 flex flex-col items-center text-center">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-4 md:mb-6">
                  <Target size={28} className="md:hidden" />
                  <Target size={32} className="hidden md:block" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-primary mb-3 md:mb-4">
                  {strategy.businessModel.activities.title}
                </h3>
                <ul className="space-y-3 md:space-y-4 text-left inline-block">
                  {strategy.businessModel.activities.items.map((item: string, index: number) => (
                    <li key={index} className="flex items-start gap-2 md:gap-3 text-gray-700">
                      <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-secondary flex-shrink-0 mt-1" />
                      <span className="text-base md:text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Strategic Pillars Section */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-primary mb-3 md:mb-4">
                {strategy.strategicPillars.title}
              </h2>
              <p className="text-gray-600 text-base md:text-lg">
                {strategy.strategicPillars.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
              {strategy.strategicPillars.pillars.map((pillar: any, index: number) => {
                // Dynamic icons based on index/context (simplified mapping)
                const icons = [ShieldCheck, TrendingUp, Globe, Zap];
                const Icon = icons[index % icons.length];

                return (
                  <div key={index} className="bg-white rounded-xl p-5 sm:p-6 md:p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 group flex flex-col items-center text-center">
                    <div className="text-primary mb-4 md:mb-6 transform group-hover:scale-110 transition-transform duration-300 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-primary/5 rounded-lg group-hover:bg-primary group-hover:text-white">
                      <Icon size={20} className="md:hidden" />
                      <Icon size={24} className="hidden md:block" />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-primary mb-2 md:mb-3">
                      {pillar.title}
                    </h3>
                    <p className="text-gray-600 mb-4 md:mb-6 text-sm min-h-0 md:min-h-[60px]">
                      {pillar.description}
                    </p>

                    <div className="border-t border-gray-100 pt-4 md:pt-6 w-full">
                      <h4 className="text-xs font-bold text-secondary uppercase tracking-wider mb-2 md:mb-3">
                        {strategy.strategicPillars.goalsLabel}
                      </h4>
                      <ul className="space-y-1.5 md:space-y-2 text-left inline-block">
                        {pillar.goals.map((goal: string, gIndex: number) => (
                          <li key={gIndex} className="text-xs text-gray-500 flex items-start gap-2">
                            <span className="w-1 h-1 bg-secondary rounded-full mt-1.5 flex-shrink-0"></span>
                            {goal}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Roadmap Section */}
        <section className="py-12 md:py-24 bg-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-20">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-primary mb-3 md:mb-4">
                {strategy.roadmap.title}
              </h2>
              <p className="text-gray-600 text-base md:text-lg">
                {strategy.roadmap.description}
              </p>
            </div>

            <div className="relative max-w-4xl mx-auto">
              {/* Vertical Line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200"></div>

              <div className="space-y-6 md:space-y-12">
                {strategy.roadmap.items.map((item: any, index: number) => (
                  <div key={index} className={`flex flex-col md:flex-row gap-4 md:gap-0 relative ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                    {/* Date Marker */}
                    <div className="absolute left-4 md:left-1/2 top-0 w-3 h-3 md:w-4 md:h-4 rounded-full border-3 md:border-4 border-white shadow-sm transform -translate-x-1/2 z-10 bg-secondary"></div>

                    {/* Content Side */}
                    <div className="w-full md:w-1/2 pl-10 md:pl-12 md:pr-12">
                      <div className={`flex flex-col ${index % 2 === 0 ? 'md:items-start md:text-left' : 'md:items-end md:text-right'}`}>
                        <div className={`bg-white p-4 sm:p-5 md:p-6 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300 w-full relative ${index % 2 === 0 ? 'origin-left' : 'origin-right'}`}>
                          <div className="flex flex-col gap-0.5 md:gap-1 mb-2 md:mb-3">
                            <span className="text-xl md:text-2xl font-bold text-primary">{item.year}</span>
                            <span className="text-xs md:text-sm font-semibold text-secondary uppercase tracking-widest">
                              {item.quarter}
                              {item.previousQuarter && (
                                <span className="ml-2 line-through text-gray-400 opacity-60">{item.previousQuarter}</span>
                              )}
                            </span>
                          </div>
                          <h4 className="text-base md:text-lg font-bold text-gray-800 mb-1.5 md:mb-2">{item.title}</h4>
                          <p className="text-gray-600 text-sm mb-3 md:mb-4">{item.description}</p>
                          <div className="flex justify-start">
                            <StatusBadge status={item.status} labels={strategy.roadmap.statusLabels} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Empty Side */}
                    <div className="hidden md:block w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-primary text-center mb-8 md:mb-16">
              {strategy.advantages.title}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {strategy.advantages.items.map((advantage: any, index: number) => (
                <div key={index} className="bg-white p-4 sm:p-5 md:p-6 rounded-lg shadow-sm border border-gray-100 flex gap-3 md:gap-4 hover:border-secondary/30 transition-colors">
                  <div className="mt-0.5 md:mt-1">
                    <CheckCircle className="text-secondary w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <h4 className="text-base md:text-lg font-bold text-primary mb-1.5 md:mb-2">{advantage.title}</h4>
                    <p className="text-gray-600 text-sm">{advantage.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </PublicLayout>
  );
};

export default StrategyPage;